import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from '../design/Modal';
import { Button } from '../design/Button';
import { Input } from '../design/Input';
import { Badge } from '../design/Badge';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { apiFetch } from '../../utils/api';
import { Loader2, Ban, UserCheck, Users, Search, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { BannedPlayersModal } from '../modals/BannedPlayersModal';
import { can } from '@/utils/permissions';

export interface KnownPlayer {
  name: string;
  uuid: string | null;
  lastSeen: number | null;
  totalSeconds: number;
  sessions: number;
  online: boolean;
  banned: boolean;
}

const fmtPlaytime = (s: number) => {
  if (!s) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtLastSeen = (ts: number | null, online: boolean) => {
  if (online) return 'Jetzt online';
  if (!ts) return 'Unbekannt';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `vor ${h} Std`;
  return `vor ${Math.floor(h / 24)} Tagen`;
};

/**
 * Kern-Liste "Bekannte Spieler" (jeder, der je auf dem Server war) mit
 * Suche + Ban/Unban-Quick-Actions. Funktioniert auch bei OFFLINE-Server
 * (das Backend editiert dann banned-players.json direkt).
 */
export const KnownPlayersList: React.FC<{ serverId: string; maxHeight?: string }> = ({ serverId, maxHeight = 'max-h-[55vh]' }) => {
  const [players, setPlayers] = useState<KnownPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const canModerate = can(userStore.currentUser, 'can_moderate_server');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/servers/${serverId}/known-players`);
      const data = await res.json();
      if (data.success) setPlayers(data.players);
      else toast.error(data.message || 'Laden fehlgeschlagen');
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => { load(); }, [load]);

  const banToggle = async (p: KnownPlayer) => {
    setActionLoading(p.name);
    const res = await serverStore.executePlayerAction(serverId, p.banned ? 'unban' : 'ban', p.name);
    if (res.success) {
      toast.success(p.banned ? `${p.name} entbannt` : `${p.name} gebannt`);
      await load();
    } else {
      toast.error(res.message || 'Aktion fehlgeschlagen');
    }
    setActionLoading(null);
  };

  const filtered = players.filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()));

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`${players.length} Spieler durchsuchen…`}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-10 text-fg-muted text-sm flex flex-col items-center">
          <Users size={32} className="mb-3 opacity-20" />
          {players.length === 0 ? 'Noch kein Spieler bekannt (war noch niemand auf dem Server?)' : 'Kein Treffer'}
        </div>
      ) : (
        <div className={`space-y-2 ${maxHeight} overflow-y-auto pr-1 custom-scrollbar`}>
          {filtered.map((p) => (
            <div
              key={p.name}
              className={`bg-black/20 border rounded-xl p-3 flex items-center justify-between gap-3 transition-all hover:border-line-strong ${p.banned ? 'border-red-500/30' : 'border-line'}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={`https://mc-heads.net/avatar/${p.name}/32`}
                  alt={p.name}
                  className={`w-9 h-9 rounded-lg shadow-md shrink-0 ${p.banned ? 'grayscale opacity-60' : ''}`}
                  style={{ imageRendering: 'pixelated' }}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm truncate">{p.name}</span>
                    {p.online && <span className="w-2 h-2 rounded-full bg-online shrink-0" title="Online" />}
                    {p.banned && <Badge variant="error" className="text-[9px] py-0 shrink-0">Gebannt</Badge>}
                  </div>
                  <div className="text-[11px] text-fg-muted flex items-center gap-2">
                    <span className={p.online ? 'text-green-400' : ''}>{fmtLastSeen(p.lastSeen, p.online)}</span>
                    {p.totalSeconds > 0 && (
                      <span className="flex items-center gap-1"><Clock size={10} /> {fmtPlaytime(p.totalSeconds)}</span>
                    )}
                  </div>
                </div>
              </div>

              {canModerate && (
                <Button
                  variant={p.banned ? 'primary' : 'danger'}
                  size="sm"
                  className={`shrink-0 ${p.banned ? 'bg-green-600 hover:bg-green-500 border-green-700/50' : ''}`}
                  disabled={actionLoading === p.name}
                  onClick={() => banToggle(p)}
                >
                  {actionLoading === p.name ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : p.banned ? (
                    <><UserCheck size={14} className="mr-1.5" /> Entbannen</>
                  ) : (
                    <><Ban size={14} className="mr-1.5" /> Bannen</>
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const KnownPlayersModal: React.FC<{ isOpen: boolean; onClose: () => void; serverId: string }> = ({ isOpen, onClose, serverId }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Bekannte Spieler" size="lg">
    {isOpen && <KnownPlayersList serverId={serverId} />}
  </Modal>
);

/**
 * Spieler-Tab bei OFFLINE-Server: keine Live-Liste, aber Moderation der
 * bekannten/gebannten Spieler funktioniert trotzdem (Datei-basiert).
 */
export const OfflinePlayersPanel: React.FC<{ serverId: string }> = ({ serverId }) => {
  const [showBanned, setShowBanned] = useState(false);
  return (
    <div className="glass-panel rounded-xl p-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Users size={18} className="text-blue-400" /> Bekannte Spieler
          </h3>
          <p className="text-xs text-fg-muted mt-0.5">
            Server ist gestoppt — Bans wirken trotzdem (Liste wird beim nächsten Start geladen).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-red-500/20 text-red-400 hover:bg-red-500/10"
          onClick={() => setShowBanned(true)}
        >
          <Ban size={13} className="mr-1.5" /> Gebannte
        </Button>
      </div>
      <KnownPlayersList serverId={serverId} maxHeight="max-h-[62vh]" />
      <BannedPlayersModal isOpen={showBanned} onClose={() => setShowBanned(false)} serverId={serverId} />
    </div>
  );
};
